import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SensitiveData } from '@/components/sensitive-data';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  BarChart2,
  Clock,
  Info,
  Minus,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Loader2,
} from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import { fetchProjects, fetchProjectById, fetchCostBudgetSummary, getDataMode } from '@/lib/data-service';
import { exportPLToExcel, fetchAllPLProjectPages } from '@/lib/export-pl-excel';
import type { ProjectFilterParams } from '@/lib/data-service';
import { getBudgetHealthThresholds } from '@/lib/budget-health-thresholds-storage';
import { getFinanceSettings } from '@/lib/finance-settings-storage';
import { calculateGP, calculateBudgetGP, GP_METHOD_LABELS, GP_METHOD_DESCRIPTIONS } from '@/lib/gp-calculation';
import { formatCurrency, healthToVariant } from '@/lib/utils';
import type { Project } from '@/types';
import type { GpCalculationMethod } from '@/lib/gp-calculation';
import type { CostBudget } from '@/types/budget.types';
import { useCompany } from '@/contexts/CompanyContext';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { ROUTES } from '@/routes';
import { PaginationControls } from '@/components/pagination-controls';
import { paginateArray, DEFAULT_PAGE_SIZE } from '@/types/pagination';
import type { PaginationMetadata, PaginationParams } from '@/types/pagination';

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight cost budget helper — avoids calling generateBudgetForProject
// which generates expensive burn-rate time-series data not needed by this page.
// Replicates only the costBudget arithmetic using fields already on Project.
// ─────────────────────────────────────────────────────────────────────────────
function computeProjectCostBudget(project: Project): CostBudget {
  const labourBudget   = project.budgetLabour  ?? 0;
  const expenseBudget  = project.budgetExpense ?? 0;
  const totalBudget = labourBudget + expenseBudget;
  const totalActual = project.actualCosts ?? 0;

  const makeCat = (
    id: number, name: string, type: 'labour' | 'expenses' | 'subcontractors',
    budget: number, actual: number, committed = 0
  ) => ({
    id, name, type, scope: 'global' as const, globalCategoryId: id,
    budget, actual, committed,
    available: budget - actual - committed,
    progressPercent: budget > 0 ? (actual / budget) * 100 : 0,
    subcategories: [],
  });

  // Use real CostCategory.actual data when available via costBudget;
  // this fallback only fires when no CostBudget exists, so attribute
  // the entire actualCosts to a single "Total Costs" row rather than
  // fabricating percentage-based splits across categories.
  return {
    projectId: project.id,
    categories: [
      makeCat(1, 'Total Costs', 'labour', totalBudget, totalActual),
    ],
    phases: [],
    totals: {
      budget:          totalBudget,
      actual:          totalActual,
      committed:       0,
      available:       totalBudget - totalActual,
      progressPercent: totalBudget > 0 ? (totalActual / totalBudget) * 100 : 0,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type RAGStatus = 'green' | 'amber' | 'red' | 'neutral';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Format a margin variance as percentage-points (pp), not %. */
function formatMarginVariance(pp: number): string {
  const prefix = pp > 0 ? '+' : '';
  return `${prefix}${pp.toFixed(1)}pp`;
}

/** Derive RAG status for a GP margin using budget health thresholds. */
function deriveMarginStatus(actualMarginPct: number, budgetMarginPct: number): RAGStatus {
  const thresholds = getBudgetHealthThresholds();
  const variancePp = budgetMarginPct - actualMarginPct; // positive = worse than budget
  // Use cost thresholds as a proxy: >redThreshold pp below budget = red, >amberThreshold = amber
  const redPp = 100 - thresholds.costRedThreshold;   // e.g. 100-110 = -10 → use absolute
  const amberPp = 100 - thresholds.costAmberThreshold;
  void redPp;
  void amberPp;
  if (variancePp > 10) return 'red';
  if (variancePp > 5) return 'amber';
  if (variancePp < -2) return 'green'; // beating budget
  return 'green';
}

/** Determine if the GP method can be applied (i.e. optional cost fields are present). */
function canApplyMethod(
  method: GpCalculationMethod,
  project: Project
): boolean {
  if (method === 'simple') return true;
  if (method === 'contribution') {
    return (
      project.actualLabourCosts !== undefined &&
      project.actualSubcontractorCosts !== undefined
    );
  }
  if (method === 'fully-loaded') {
    return project.actualOverheadAbsorption !== undefined;
  }
  return true;
}

/** Effective method: falls back to 'simple' if optional fields are missing. */
function effectiveMethod(
  method: GpCalculationMethod,
  project: Project
): GpCalculationMethod {
  return canApplyMethod(method, project) ? method : 'simple';
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function HealthBadge({ status }: { status: RAGStatus }) {
  if (status === 'neutral') return null;
  const variant = healthToVariant(status);
  const label = status === 'green' ? 'On Track' : status === 'amber' ? 'At Risk' : 'Off Track';
  return (
    <Badge variant={variant} className="text-xs gap-1.5">
      <StatusIndicator variant={variant === 'destructive' ? 'error' : variant} size="sm" />
      {label}
    </Badge>
  );
}



/** A table row for a P&L line. */
function PLRow({
  label,
  budget,
  actual,
  variance,
  isPercent = false,
  invertVarianceColour = false,
  bold = false,
  indent = false,
  dimmed = false,
  tooltip,
  isPercentPoints = false,
}: {
  label: string;
  budget: number | null;
  actual: number | null;
  variance: number | null;
  isPercent?: boolean;
  invertVarianceColour?: boolean;
  bold?: boolean;
  indent?: boolean;
  dimmed?: boolean;
  tooltip?: string;
  isPercentPoints?: boolean;
}) {
  const formatVal = (v: number | null): React.ReactNode => {
    if (v === null) return <span className="text-muted-foreground italic">—</span>;
    if (isPercentPoints) return formatMarginVariance(v);
    if (isPercent) {
      const prefix = v > 0 ? '' : '';
      return `${prefix}${v.toFixed(1)}%`;
    }
    return formatCurrency(v);
  };

  const formatVariance = (v: number | null): React.ReactNode => {
    if (v === null) return <span className="text-muted-foreground italic">—</span>;
    let formatted: string;
    if (isPercentPoints) {
      formatted = formatMarginVariance(v);
    } else if (isPercent) {
      const prefix = v > 0 ? '+' : '';
      formatted = `${prefix}${v.toFixed(1)}pp`;
    } else {
      const prefix = v > 0 ? '+' : '';
      formatted = `${prefix}${formatCurrency(v)}`;
    }
    const isOnTarget = Math.abs(v) < (isPercent || isPercentPoints ? 0.5 : 1);
    let colourClass = 'text-muted-foreground';
    if (!isOnTarget) {
      // For cost rows: positive variance = over budget = bad.
      // invertVarianceColour is used for revenue/GP: positive = good.
      const isBad = invertVarianceColour ? v < 0 : v > 0;
      colourClass = isBad ? 'text-destructive' : 'text-success-600 dark:text-success-500';
    }
    return <span className={colourClass}>{formatted}</span>;
  };

  const labelEl = (
    <span className={`${bold ? 'font-semibold' : ''} ${dimmed ? 'text-muted-foreground' : ''}`}>
      {label}
    </span>
  );

  return (
    <TableRow className={bold ? 'bg-muted/30' : undefined}>
      <TableCell
        className={`${indent ? 'pl-8' : ''} ${bold ? 'font-semibold' : ''} text-sm`}
      >
        {tooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center gap-1 cursor-default">
                  {labelEl}
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          labelEl
        )}
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>{formatVal(budget)}</SensitiveData>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm">
        <SensitiveData>{formatVal(actual)}</SensitiveData>
      </TableCell>
      <TableCell className="text-right tabular-nums text-sm pr-3">
        <SensitiveData>{formatVariance(variance)}</SensitiveData>
      </TableCell>
    </TableRow>
  );
}

/** Section divider row inside the table. */
function PLSectionHeader({ label }: { label: string }) {
  return (
    <TableRow className="bg-muted/50 hover:bg-muted/50">
      <TableCell colSpan={4} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground py-2 pl-3">
        {label}
      </TableCell>
    </TableRow>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Portfolio Landing View (shown when no project is selected)
// ─────────────────────────────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc';
type SortCol = 'health' | 'name' | 'actualMargin' | 'budgetMargin' | 'marginVariance' | 'actualGP' | 'hoursVariance';

interface EnrichedPLProject {
  project: Project;
  actualMarginPct: number;
  budgetMarginPct: number;
  marginVariancePp: number;
  actualGP: number;
  budgetGP: number;
  hoursVariance: number;
  status: RAGStatus;
}

function SortIcon({ col, active, dir }: { col: SortCol; active: SortCol; dir: SortDir }) {
  if (col !== active) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline" />;
  return dir === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1 inline" />
    : <ArrowDown className="h-3 w-3 ml-1 inline" />;
}

const STORAGE_KEY_PREFIX = 'project-pl-report:';

function PLPortfolioView({
  projects,
  onSelectProject,
  onRefetch,
  pagination: serverPagination,
}: {
  projects: Project[];
  onSelectProject: (id: number) => void;
  onRefetch?: (filters: ProjectFilterParams, paginationParams?: PaginationParams) => void;
  pagination?: PaginationMetadata | null;
}) {
  const [isExporting, setIsExporting] = useState(false);
  const isLive = getDataMode() === 'live';
  const financeSettings  = useMemo(() => getFinanceSettings(), []);
  const thresholds       = useMemo(() => getBudgetHealthThresholds(), []);
  // Filters, sorting and the current page are persisted for the session so
  // drilling into a project and navigating back restores the same view.
  const [searchTerm, setSearchTerm] = usePersistedState(`${STORAGE_KEY_PREFIX}search`, '');
  const [billableFilter, setBillableFilter] = usePersistedState<string>(`${STORAGE_KEY_PREFIX}billable`, 'all');
  const [sortCol,  setSortCol]  = usePersistedState<SortCol>(`${STORAGE_KEY_PREFIX}sort-col`, 'health');
  const [sortDir,  setSortDir]  = usePersistedState<SortDir>(`${STORAGE_KEY_PREFIX}sort-dir`, 'asc');

  // Pagination state (client-side for demo/tour, requested page for live)
  const [currentPage, setCurrentPage] = usePersistedState(`${STORAGE_KEY_PREFIX}page`, 1);
  const [pageSize, setPageSize] = usePersistedState(`${STORAGE_KEY_PREFIX}page-size`, DEFAULT_PAGE_SIZE);
  const [isFetching, setIsFetching] = useState(false);

  // Reset page when search changes (demo/tour only)
  const prevSearchTerm = useRef('');
  if (searchTerm !== prevSearchTerm.current) {
    prevSearchTerm.current = searchTerm;
    if (!isLive && currentPage !== 1) setCurrentPage(1);
  }

  // Build current filter params for server-side requests
  const buildFilters = useCallback((): ProjectFilterParams => {
    const filters: ProjectFilterParams = {};
    if (searchTerm.trim()) filters.search = searchTerm.trim();
    if (billableFilter !== 'all') filters.billable = billableFilter;
    return filters;
  }, [searchTerm, billableFilter]);

  const filtersRef = useRef<ProjectFilterParams>({});
  filtersRef.current = buildFilters();

  // Server-side refetch when search changes (live mode only). The first run
  // restores the persisted page instead of resetting to page 1.
  const isRestoringRef = useRef(true);
  useEffect(() => {
    if (!isLive || !onRefetch) return;
    const restoring = isRestoringRef.current;
    isRestoringRef.current = false;
    if (!restoring && currentPage !== 1) setCurrentPage(1);
    const page = restoring ? currentPage : 1;
    const handler = setTimeout(() => {
      setIsFetching(true);
      Promise.resolve(onRefetch(filtersRef.current, { page, pageSize }))
        .finally(() => setIsFetching(false));
    }, restoring ? 0 : 300);
    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLive, onRefetch, searchTerm, billableFilter]);

  const enriched = useMemo<EnrichedPLProject[]>(() =>
    projects.map(p => {
      const method = effectiveMethod(financeSettings.gpCalculationMethod, p);

      // Actual GP
      const actualGPResult = calculateGP(
        {
          revenue: p.actualSales ?? 0,
          totalDirectCosts: p.actualCosts ?? 0,
          labourCosts: p.actualLabourCosts,
          subcontractorCosts: p.actualSubcontractorCosts,
          overheadAbsorption: p.actualOverheadAbsorption,
        },
        method,
      );

      // Budget GP — derived directly from Project fields, no expensive budget generation needed.
      // totalBudgetCosts = budgetLabour + budgetExpense (all direct cost budgets on the Project).
      const budgetRevenue    = p.budgetRevenue ?? 0;
      const totalBudgetCosts = (p.budgetLabour ?? 0) + (p.budgetExpense ?? 0);
      const budgetGPResult   = calculateBudgetGP(
        { revenue: budgetRevenue, totalDirectCosts: totalBudgetCosts },
        'simple',
      );

      const actualMarginPct  = actualGPResult.grossMarginPct;
      const budgetMarginPct  = budgetGPResult.grossMarginPct;
      const marginVariancePp = actualMarginPct - budgetMarginPct;
      const actualGP         = actualGPResult.grossProfit;
      const budgetGP         = budgetGPResult.grossProfit;

      // Hours
      const actualHours   = p.actualHours  ?? 0;
      const budgetHoursVal = p.budgetHours ?? 0;
      const hoursVariance = actualHours - budgetHoursVal;

      // RAG: driven by how many pp below budget the actual margin is
      const belowBudget = budgetMarginPct - actualMarginPct; // positive = worse
      const status: RAGStatus =
        belowBudget >= thresholds.costRedThreshold   ? 'red'
        : belowBudget >= thresholds.costAmberThreshold ? 'amber'
        : 'green';

      return { project: p, actualMarginPct, budgetMarginPct, marginVariancePp, actualGP, budgetGP, hoursVariance, status };
    }),
    [projects, financeSettings, thresholds],
  );

  // Summary counts — in live mode use pagination totalItems for total
  const counts = useMemo(() => ({
    total: isLive && serverPagination ? serverPagination.totalItems : enriched.length,
    green: enriched.filter(e => e.status === 'green').length,
    amber: enriched.filter(e => e.status === 'amber').length,
    red:   enriched.filter(e => e.status === 'red').length,
  }), [enriched, isLive, serverPagination]);

  // Search (client-side for demo/tour only; live mode uses server-side search)
  const filtered = useMemo(() => {
    if (isLive) return enriched;
    let result = enriched;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter(e =>
        e.project.name.toLowerCase().includes(q) ||
        (e.project.code ?? '').toLowerCase().includes(q),
      );
    }
    if (billableFilter === 'billable') result = result.filter(e => e.project.billable !== false);
    if (billableFilter === 'non-billable') result = result.filter(e => e.project.billable === false);
    return result;
  }, [enriched, searchTerm, billableFilter, isLive]);

  // Sort
  const sorted = useMemo(() => {
    const STATUS_ORDER: Record<RAGStatus, number> = { red: 0, amber: 1, green: 2, neutral: 3 };
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'health':         cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]; break;
        case 'name':           cmp = a.project.name.localeCompare(b.project.name); break;
        case 'actualMargin':   cmp = a.actualMarginPct - b.actualMarginPct; break;
        case 'budgetMargin':   cmp = a.budgetMarginPct - b.budgetMarginPct; break;
        case 'marginVariance': cmp = a.marginVariancePp - b.marginVariancePp; break;
        case 'actualGP':       cmp = a.actualGP - b.actualGP; break;
        case 'hoursVariance':  cmp = a.hoursVariance - b.hoursVariance; break;
      }
      if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
      return a.project.name.localeCompare(b.project.name);
    });
  }, [filtered, sortCol, sortDir]);

  // Paginate — client-side for demo/tour, server data used as-is for live
  const { data: paginatedRows, pagination } = useMemo(
    () => isLive && serverPagination
      ? { data: sorted, pagination: serverPagination }
      : paginateArray(sorted, currentPage, pageSize),
    [sorted, currentPage, pageSize, isLive, serverPagination]
  );

  // Server-side pagination handlers (live mode only)
  const handleServerPageChange = useCallback((page: number) => {
    if (!onRefetch || !serverPagination) return;
    setCurrentPage(page);
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page, pageSize: serverPagination.pageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch, serverPagination, setCurrentPage]);

  const handleServerPageSizeChange = useCallback((newPageSize: number) => {
    if (!onRefetch) return;
    setCurrentPage(1);
    setPageSize(newPageSize);
    setIsFetching(true);
    Promise.resolve(onRefetch(filtersRef.current, { page: 1, pageSize: newPageSize }))
      .finally(() => setIsFetching(false));
  }, [onRefetch, setCurrentPage, setPageSize]);

  const handlePageChange = useCallback((page: number) => setCurrentPage(page), [setCurrentPage]);
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, [setPageSize, setCurrentPage]);

  function handleSort(col: SortCol) {
    if (col === sortCol) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    if (!isLive) setCurrentPage(1);
  }

  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      if (!isLive) {
        await exportPLToExcel(projects);
        toast.success('Export complete — file downloaded.');
        return;
      }

      const allProjects = await fetchAllPLProjectPages(filtersRef.current);
      await exportPLToExcel(allProjects);
      toast.success(`Export complete — ${allProjects.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, isLive, projects]);

  function SortHeader({ col, children, className }: { col: SortCol; children: React.ReactNode; className?: string }) {
    return (
      <TableHead
        className={`cursor-pointer select-none hover:text-foreground ${className ?? ''}`}
        onClick={() => handleSort(col)}
      >
        {children}
        <SortIcon col={col} active={sortCol} dir={sortDir} />
      </TableHead>
    );
  }

  return (
    <Card data-tour="project-pl-table">
      {/* Filters toolbar */}
      <div className="flex flex-wrap items-center gap-3 p-6 pb-4" data-tour="project-pl-filters">
        <div className="relative w-56">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search projects…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/* Billable filter */}
          <Select value={billableFilter} onValueChange={setBillableFilter}>
            <SelectTrigger className="h-8 w-36 text-sm">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="non-billable">Non-billable</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={isExporting}
            className="h-8 w-8 p-0"
            aria-label="Download to Excel"
            title="Download to Excel"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Portfolio table — flush to card edges */}
      <CardContent className="p-0">
        <div className={`overflow-x-auto border-t${isFetching ? ' opacity-50 pointer-events-none' : ''}`}>
          <Table>
            <TableHeader divided>
              <TableRow>
                <SortHeader col="health" className="w-[120px]">Margin Status</SortHeader>
                <SortHeader col="name">Project</SortHeader>
                <SortHeader col="actualMargin" className="text-right">Actual Margin</SortHeader>
                <SortHeader col="budgetMargin" className="text-right">Budget Margin</SortHeader>
                <SortHeader col="marginVariance" className="text-right">Variance (pp)</SortHeader>
                <SortHeader col="actualGP" className="text-right">Actual GP £</SortHeader>
                <SortHeader col="hoursVariance" className="text-right">Hours Var.</SortHeader>
                <TableHead>Billable</TableHead>
              </TableRow>
            </TableHeader>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-muted-foreground text-sm">
                      {searchTerm ? `No projects match "${searchTerm}"` : 'No projects found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map(({ project, actualMarginPct, budgetMarginPct, marginVariancePp, actualGP, hoursVariance, status }) => (
                    <TableRow
                      key={project.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => onSelectProject(project.id)}
                    >
                      {/* Margin Status */}
                      <TableCell>
                        <HealthBadge status={status} />
                      </TableCell>

                      {/* Project name + code */}
                      <TableCell>
                        <div className="font-medium text-sm">{project.name}</div>
                        {project.code && (
                          <div className="text-xs text-muted-foreground">{project.code}</div>
                        )}
                      </TableCell>

                      {/* Actual margin % */}
                      <TableCell className="text-right text-sm">
                        <SensitiveData>
                          <span className={
                            actualMarginPct < 0 ? 'text-destructive' :
                            actualMarginPct < 10 ? 'text-warning-600 dark:text-warning-500' : ''
                          }>
                            {actualMarginPct.toFixed(1)}%
                          </span>
                        </SensitiveData>
                      </TableCell>

                      {/* Budget margin % */}
                      <TableCell className="text-right text-sm text-muted-foreground">
                        <SensitiveData>{budgetMarginPct.toFixed(1)}%</SensitiveData>
                      </TableCell>

                      {/* Margin variance pp */}
                      <TableCell className="text-right text-sm font-medium">
                        <SensitiveData>
                          <span className={
                            marginVariancePp < -5  ? 'text-destructive' :
                            marginVariancePp < -2  ? 'text-warning-600 dark:text-warning-500' :
                            marginVariancePp >= 0  ? 'text-success-600 dark:text-success-500' : ''
                          }>
                            {formatMarginVariance(marginVariancePp)}
                          </span>
                        </SensitiveData>
                      </TableCell>

                      {/* Actual GP £ */}
                      <TableCell className="text-right text-sm">
                        <SensitiveData>
                          <span className={actualGP < 0 ? 'text-destructive' : ''}>
                            {formatCurrency(actualGP)}
                          </span>
                        </SensitiveData>
                      </TableCell>

                      {/* Hours variance */}
                      <TableCell className="text-right text-sm">
                        <SensitiveData>
                          <span className={
                            hoursVariance > 0 ? 'text-destructive' :
                            hoursVariance < 0 ? 'text-success-600 dark:text-success-500' : ''
                          }>
                            {hoursVariance === 0 ? (
                              <Minus className="h-3 w-3 inline text-muted-foreground" />
                            ) : (
                              `${hoursVariance > 0 ? '+' : ''}${hoursVariance} hrs`
                            )}
                          </span>
                        </SensitiveData>
                      </TableCell>

                      {/* Billable */}
                      <TableCell>
                        {(project.billable ?? true)
                          ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
                          : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>
                        }
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        {/* Pagination inside card */}
        {(isLive && serverPagination && serverPagination.totalItems > 0) || (!isLive && pagination.totalPages > 1) ? (
          <div className="flex items-center px-6 py-3 border-t">
            <div className="w-full">
              {isLive && serverPagination && serverPagination.totalItems > 0 ? (
                <PaginationControls
                  pagination={serverPagination}
                  onPageChange={handleServerPageChange}
                  onPageSizeChange={handleServerPageSizeChange}
                  showPageSizeSelector
                />
              ) : (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  showPageSizeSelector
                />
              )}
            </div>
          </div>
        ) : null}
      </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Empty States
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <div className="p-3 rounded-full bg-muted">
          <TrendingUp className="h-6 w-6 text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground max-w-sm">{message}</p>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────

export function ProjectPLReportPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentCompany } = useCompany();
  const { handleApiError } = useErrorHandler({ useToast: true });

  // Read ?managerId= from URL (passed by homepage PM quick link)
  const initManagerId = searchParams.get('managerId') ? Number(searchParams.get('managerId')) : null;

  // ── State ─────────────────────────────────────────────────────────────────
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [expandedCostSection, setExpandedCostSection] = useState(false);

  // Read ?reportProjectId= on mount (safe — DeepLinkHandler only intercepts
  // ?projectId=, not ?reportProjectId=). This allows deep-linking from the
  // project reports tab via navigate(`/reports/project-pl?reportProjectId=6`).
  const initReportProjectId = searchParams.get('reportProjectId');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    initReportProjectId ? Number(initReportProjectId) : null
  );

  // Deep-linked project fetched by ID (may not be in the paginated list)
  const [deepLinkedProject, setDeepLinkedProject] = useState<Project | null>(null);

  useEffect(() => {
    if (initReportProjectId) {
      fetchProjectById(Number(initReportProjectId)).then((p) => {
        if (p) setDeepLinkedProject(p as Project);
      }).catch(() => { /* ignore — will fall back to paginated list */ });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Project selection handlers ────────────────────────────────────────────
  // Uses ?reportProjectId= (safe — DeepLinkHandler only intercepts ?projectId=)
  const handleSelectProject = (id: number) => {
    setSelectedProjectId(id);
    const params = new URLSearchParams(searchParams);
    params.set('reportProjectId', String(id));
    setSearchParams(params);
  };

  const handleBack = () => {
    setSelectedProjectId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('reportProjectId');
    setSearchParams(params);
  };

  // Keep the selected project in sync with the URL so browser back/forward
  // moves between the drill-down and the portfolio grid.
  useEffect(() => {
    const idParam = searchParams.get('reportProjectId');
    setSelectedProjectId(idParam ? Number(idParam) : null);
  }, [searchParams]);

  // Reset cost expansion on project change
  useEffect(() => {
    setExpandedCostSection(false);
  }, [selectedProjectId]);

  // Helper to extract projects + pagination from API response
  const applyProjectResult = useCallback((projectResult: Project[] | { data: Project[]; pagination: PaginationMetadata }) => {
    if (projectResult && typeof projectResult === 'object' && 'data' in projectResult && !Array.isArray(projectResult)) {
      setProjects(projectResult.data);
      setPagination(projectResult.pagination);
    } else {
      setProjects(Array.isArray(projectResult) ? projectResult : []);
      setPagination(null);
    }
  }, []);

  // ── Load projects (server-side paginated in live mode) ─────────────────────
  useEffect(() => {
    setLoadingProjects(true);
    fetchProjects('projects', { page: 1, pageSize: DEFAULT_PAGE_SIZE })
      .then((result) => applyProjectResult(result))
      .catch((err) => handleApiError(err, 'Loading projects'))
      .finally(() => setLoadingProjects(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  // Re-fetch projects with server-side filters + pagination (called by PLPortfolioView)
  const handleRefetch = useCallback(async (filters: ProjectFilterParams, paginationParams?: PaginationParams) => {
    try {
      const effectivePagination = paginationParams ?? { page: 1, pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE };
      const projectResult = await fetchProjects('projects', effectivePagination, filters);
      applyProjectResult(projectResult);
    } catch (err) {
      handleApiError(err, 'Failed to filter projects');
    }
  }, [applyProjectResult, handleApiError, pagination?.pageSize]);

  // ── Finance settings ──────────────────────────────────────────────────────
  const financeSettings = useMemo(() => getFinanceSettings(), []);
  const gpMethod: GpCalculationMethod = financeSettings.gpCalculationMethod;
  const revenueDisplayMode = financeSettings.revenueDisplayMode;

  // ── Selected project + budget data ───────────────────────────────────────
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? deepLinkedProject ?? null,
    [projects, selectedProjectId, deepLinkedProject]
  );

  // Fetch real cost budget data (with categories) from the API when a project
  // is selected.  Falls back to the lightweight arithmetic helper when the
  // API returns nothing (i.e. no CostBudget exists for this project).
  const [liveCostBudget, setLiveCostBudget] = useState<CostBudget | undefined>(undefined);

  useEffect(() => {
    if (!selectedProject) {
      setLiveCostBudget(undefined);
      return;
    }
    let cancelled = false;
    fetchCostBudgetSummary(selectedProject.id)
      .then((data) => {
        if (!cancelled) setLiveCostBudget(data ?? undefined);
      })
      .catch(() => {
        if (!cancelled) setLiveCostBudget(undefined);
      });
    return () => { cancelled = true; };
  }, [selectedProject]);

  const costBudget = liveCostBudget ?? (selectedProject ? computeProjectCostBudget(selectedProject) : undefined);
  const hasCostBudget = !!costBudget && costBudget.categories.length > 0;

  // ── GP calculations ───────────────────────────────────────────────────────
  const appliedMethod = useMemo(
    () => (selectedProject ? effectiveMethod(gpMethod, selectedProject) : gpMethod),
    [gpMethod, selectedProject]
  );
  const isFallback = appliedMethod !== gpMethod;

  const actualGPResult = useMemo(() => {
    if (!selectedProject) return null;
    return calculateGP(
      {
        revenue: selectedProject.actualSales ?? 0,
        totalDirectCosts: selectedProject.actualCosts ?? 0,
        labourCosts: selectedProject.actualLabourCosts,
        subcontractorCosts: selectedProject.actualSubcontractorCosts,
        overheadAbsorption: selectedProject.actualOverheadAbsorption,
      },
      appliedMethod
    );
  }, [selectedProject, appliedMethod]);

  const budgetGPResult = useMemo(() => {
    if (!selectedProject) return null;
    const budgetRevenue = selectedProject.budgetRevenue ?? 0;
    const totalBudgetCosts = costBudget?.totals.budget ?? 0;
    return calculateBudgetGP(
      {
        revenue: budgetRevenue,
        totalDirectCosts: totalBudgetCosts,
        // Budget doesn't have breakdown by labour/subs/overhead, so always use simple for budget
      },
      'simple'
    );
  }, [selectedProject, costBudget]);

  // ── Variance calculations ─────────────────────────────────────────────────
  const actualRevenue = selectedProject?.actualSales ?? 0;
  const budgetRevenue = selectedProject?.budgetRevenue ?? 0;
  const revenueVariance = actualRevenue - budgetRevenue;
  const revenueVariancePct = budgetRevenue !== 0 ? (revenueVariance / budgetRevenue) * 100 : 0;

  const actualCosts = selectedProject?.actualCosts ?? 0;
  const budgetCosts = costBudget?.totals.budget ?? 0;
  const costVariance = actualCosts - budgetCosts;
  const costVariancePct = budgetCosts !== 0 ? (costVariance / budgetCosts) * 100 : 0;

  const actualHours = selectedProject?.actualHours ?? 0;
  const budgetHours = selectedProject?.budgetHours ?? 0;
  const hoursVariance = actualHours - budgetHours;

  const actualGP = actualGPResult?.grossProfit ?? 0;
  const actualGPMargin = actualGPResult?.grossMarginPct ?? 0;
  const budgetGP = budgetGPResult?.grossProfit ?? 0;
  const budgetGPMargin = budgetGPResult?.grossMarginPct ?? 0;
  const gpVariance = actualGP - budgetGP;
  const marginVariancePp = actualGPMargin - budgetGPMargin;

  // ── Summary card RAG ──────────────────────────────────────────────────────
  const thresholds = useMemo(() => getBudgetHealthThresholds(), []);
  const costPct = budgetCosts > 0 ? (actualCosts / budgetCosts) * 100 : 0;
  const costStatus: RAGStatus =
    costPct >= thresholds.costRedThreshold ? 'red' :
    costPct >= thresholds.costAmberThreshold ? 'amber' : 'green';

  // Revenue: positive variance = good (under-invoiced = amber for completed projects)
  // statusId 5 = Complete (per ProjectStatusId type: 1=Active,2=On Hold,3=At Risk,4=Delayed,5=Complete,6=Cancelled,7=Draft)
  const isComplete = selectedProject?.statusId === 5;
  const revenueStatus: RAGStatus =
    revenueVariance < 0 && isComplete ? 'red' :
    revenueVariance < 0 ? 'amber' :
    revenueVariance > budgetRevenue * 0.05 ? 'green' : 'green';

  const gpStatus: RAGStatus = deriveMarginStatus(actualGPMargin, budgetGPMargin);

  const hoursStatus: RAGStatus =
    budgetHours > 0 ? (
      (actualHours / budgetHours) * 100 >= thresholds.costRedThreshold ? 'red' :
      (actualHours / budgetHours) * 100 >= thresholds.costAmberThreshold ? 'amber' : 'green'
    ) : 'neutral';

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 pb-10 print:pb-0">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <PageHeaderBlock
        title="Project P&L Report"
        subtitle={
          selectedProject
            ? `${selectedProject.code ? `${selectedProject.code} — ` : ''}${selectedProject.name}`
            : 'Planned vs actual profit & loss by project.'
        }
      />

      {/* ── Control bar — only shown when a project is selected ─────────── */}
      {selectedProjectId !== null && (
        <div className="flex flex-row gap-3 items-center print:hidden">
          {/* Back button */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={handleBack}
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            All Projects
          </Button>

          <div className="flex-1" />

          {/* GP Method badge */}
          {selectedProject && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary cursor-default border border-primary/20">
                    <Info className="h-3 w-3" />
                    {GP_METHOD_LABELS[appliedMethod]}
                    {isFallback && ' (fallback)'}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="font-medium mb-1">{GP_METHOD_LABELS[appliedMethod]}</p>
                  <p className="text-xs">{GP_METHOD_DESCRIPTIONS[appliedMethod]}</p>
                  {isFallback && (
                    <p className="text-xs text-warning-500 mt-1">
                      ⚠ Some cost breakdown fields are unavailable for this project. Showing Simple Margin instead of{' '}
                      {GP_METHOD_LABELS[gpMethod]}.
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      {/* Fallback warning banner */}
      {selectedProject && isFallback && (
        <div className="flex items-start gap-3 rounded-lg border border-warning-200 bg-warning-50 dark:bg-warning-950/20 dark:border-warning-800 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-warning-600 dark:text-warning-500 shrink-0 mt-0.5" />
          <span className="text-warning-800 dark:text-warning-300">
            Cost breakdown unavailable for this project — GP shown using Simple Margin instead of{' '}
            <strong>{GP_METHOD_LABELS[gpMethod]}</strong>.
          </span>
        </div>
      )}

      {/* ── Portfolio landing view (no project selected) ─────────────────── */}
      {selectedProjectId === null && (
        loadingProjects ? (
          <div className="space-y-4">
            <div className="flex gap-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-6 w-24 rounded-full" />
              ))}
              <Skeleton className="h-8 w-56 ml-auto rounded-md" />
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="p-4 space-y-3">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="flex gap-4">
                      <Skeleton className="h-4 w-8" />
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-4 w-16 ml-auto" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <PLPortfolioView
            projects={initManagerId ? projects.filter(p => p.projectManagerId === initManagerId) : projects}
            onSelectProject={handleSelectProject}
            onRefetch={handleRefetch}
            pagination={pagination}
          />
        )
      )}

      {/* ── Loading state (project selected, data loading) ────────────────── */}
      {selectedProjectId !== null && loadingProjects && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-l-4 border-l-border">
              <CardContent className="pt-4 pb-4">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-7 w-32" />
                  <Skeleton className="h-3 w-40" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Report content ────────────────────────────────────────────────── */}
      {selectedProject && !loadingProjects && (
        <>
          {/* ── Summary Cards ──────────────────────────────────────────── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <CompactKpiCard
              title="Actual GP%"
              value={<SensitiveData><span>{actualGPMargin.toFixed(1)}%</span></SensitiveData>}
              description={`Budget: ${budgetGPMargin.toFixed(1)}% · Variance: ${formatMarginVariance(marginVariancePp)}`}
              icon={TrendingUp}
              accentVariant={gpStatus === 'green' ? 'success' : gpStatus === 'amber' ? 'warning' : gpStatus === 'red' ? 'error' : 'info'}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Revenue Variance"
              value={
                <SensitiveData>
                  <span>{revenueVariance >= 0 ? '+' : ''}{formatCurrency(revenueVariance)}</span>
                </SensitiveData>
              }
              description={`${revenueVariancePct >= 0 ? '+' : ''}${revenueVariancePct.toFixed(1)}% vs budget`}
              icon={BarChart2}
              accentVariant={revenueStatus === 'green' ? 'success' : revenueStatus === 'amber' ? 'warning' : revenueStatus === 'red' ? 'error' : 'info'}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Cost Variance"
              value={
                <SensitiveData>
                  <span>{costVariance >= 0 ? '+' : ''}{formatCurrency(costVariance)}</span>
                </SensitiveData>
              }
              description={`${costVariancePct >= 0 ? '+' : ''}${costVariancePct.toFixed(1)}% vs budget`}
              icon={TrendingDown}
              accentVariant={costStatus === 'green' ? 'success' : costStatus === 'amber' ? 'warning' : costStatus === 'red' ? 'error' : 'info'}
              className="shadow-none"
            />
            <CompactKpiCard
              title="Hours Variance"
              value={
                <SensitiveData>
                  <span>{hoursVariance >= 0 ? '+' : ''}{hoursVariance} hrs</span>
                </SensitiveData>
              }
              description={`Actual: ${actualHours} hrs · Budget: ${budgetHours} hrs`}
              icon={Clock}
              accentVariant={hoursStatus === 'green' ? 'success' : hoursStatus === 'amber' ? 'warning' : hoursStatus === 'red' ? 'error' : 'info'}
              className="shadow-none"
            />
          </div>

          {/* ── P&L Table ──────────────────────────────────────────────── */}
          <Card>
            <CardHeader className="pb-2 print:pb-0">
              <CardTitle className="text-base font-semibold">
                Profit & Loss — {selectedProject.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pt-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%] pl-3">Line Item</TableHead>
                    <TableHead className="text-right">Budget</TableHead>
                    <TableHead className="text-right">Actual</TableHead>
                    <TableHead className="text-right pr-3">Variance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* ── Revenue ────────────────────────────────────────── */}
                  <PLSectionHeader label="Revenue" />

                  <PLRow
                    label="Contract Value (Budget Revenue)"
                    budget={budgetRevenue}
                    actual={null}
                    variance={null}
                    tooltip="Budget revenue — the planned top-line value of this project."
                  />

                  {(revenueDisplayMode === 'both' || revenueDisplayMode === 'invoiced') && (
                    <PLRow
                      label="Invoiced Revenue"
                      budget={budgetRevenue}
                      actual={actualRevenue}
                      variance={revenueVariance}
                      invertVarianceColour
                      indent
                    />
                  )}

                  {(revenueDisplayMode === 'both' || revenueDisplayMode === 'recognised') && (
                    <PLRow
                      label="Recognised Revenue"
                      budget={budgetRevenue}
                      actual={null}
                      variance={null}
                      indent
                      dimmed
                      tooltip="Recognised revenue is not yet available in Phase 1. Will be wired when Revenue Recognition is built."
                    />
                  )}

                  {/* ── Costs ──────────────────────────────────────────── */}
                  <PLSectionHeader label="Direct Costs" />

                  <PLRow
                    label="Total Direct Costs"
                    budget={budgetCosts}
                    actual={actualCosts}
                    variance={costVariance}
                    bold
                  />

                  {/* Cost category breakdown toggle */}
                  {hasCostBudget && (
                    <>
                      <TableRow
                        className="hover:bg-muted/50 cursor-pointer"
                        onClick={() => setExpandedCostSection((v) => !v)}
                      >
                        <TableCell colSpan={4} className="pl-8 py-1.5">
                          <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                            {expandedCostSection ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                            {expandedCostSection ? 'Hide' : 'Show'} cost breakdown by category
                          </button>
                        </TableCell>
                      </TableRow>

                      {expandedCostSection &&
                        costBudget!.categories.map((cat) => {
                          const catVariance = cat.actual - cat.budget;
                          return (
                            <PLRow
                              key={cat.id}
                              label={cat.name}
                              budget={cat.budget}
                              actual={cat.actual}
                              variance={catVariance}
                              indent
                            />
                          );
                        })}
                    </>
                  )}

                  {!hasCostBudget && (
                    <TableRow>
                      <TableCell colSpan={4} className="pl-8 py-1.5 text-xs text-muted-foreground italic">
                        No cost category budget available for this project.
                      </TableCell>
                    </TableRow>
                  )}

                  {/* ── Gross Profit ───────────────────────────────────── */}
                  <PLSectionHeader label="Gross Profit" />

                  <PLRow
                    label="Budget Gross Profit"
                    budget={budgetGP}
                    actual={null}
                    variance={null}
                    bold
                  />

                  <PLRow
                    label={`Actual Gross Profit (${GP_METHOD_LABELS[appliedMethod]}${isFallback ? ' — fallback' : ''})`}
                    budget={budgetGP}
                    actual={actualGP}
                    variance={gpVariance}
                    invertVarianceColour
                    bold
                  />

                  <PLRow
                    label="Budget GP Margin"
                    budget={budgetGPMargin}
                    actual={null}
                    variance={null}
                    isPercent
                    indent
                  />

                  <PLRow
                    label="Actual GP Margin"
                    budget={budgetGPMargin}
                    actual={actualGPMargin}
                    variance={marginVariancePp}
                    isPercent
                    invertVarianceColour
                    indent
                    isPercentPoints={false}
                  />

                  {/* ── Hours ──────────────────────────────────────────── */}
                  <PLSectionHeader label="Hours" />

                  <PLRow
                    label="Total Hours"
                    budget={budgetHours}
                    actual={actualHours}
                    variance={hoursVariance}
                    invertVarianceColour={false}
                  />
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* ── Project metadata footer ──────────────────────────────── */}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground print:hidden">
            {selectedProject.clientNames && (
              <span>
                <span className="font-medium">Client:</span> {selectedProject.clientNames}
              </span>
            )}
            {selectedProject.type && (
              <span>
                <span className="font-medium">Type:</span> {selectedProject.type}
              </span>
            )}
            {selectedProject.statusId && (
              <span>
                <span className="font-medium">Status ID:</span> {selectedProject.statusId}
              </span>
            )}
            {selectedProject.completionPercent !== undefined && (
              <span>
                <span className="font-medium">Completion:</span> {selectedProject.completionPercent}%
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
